import Button from 'admin/components/atoms/Buttons';
import CheckBox from 'admin/components/atoms/CheckBox';
import Select from 'admin/components/atoms/Select';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import AdminUser from '~/javascripts/admin/models/admin_user';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { ShopsQueryParams } from '..';
import LabeledTextBox from '../../../molecules/LabeledTextBox';
import SelectedPrivateMoneyDisplay from '../../../organisms/SelectedPrivateMoneyDisplay';

type Query = {
  shop_name: string | null | undefined;
  shop_address: string | null | undefined;
  shop_tel: string | null | undefined;
  shop_email: string | null | undefined;
  is_include_members: boolean | undefined;
};

type Props = {
  privateMoneyId?: string;
  query: Query;
  onSearch: (query: Query) => void;
  onClear: () => void;
};

type FormFilterParams = ShopsQueryParams;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  ${LabeledSelect as any} {
    margin: 0px 10px;
  }
  ${Select as any} {
    width: 300px;
  }
`;

const FilterActions = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  gap: 10px;
`;

const Label = styled.label`
  font-weight: 600;
  margin-right: 5px;
  margin-bottom: 0;
`;
const FormItem = styled.div`
  margin: 5px;
`;

const removeEmptyKeysFromQuery = (obj: Record<string, unknown>): Query => {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && v !== '')) as Query;
};

const FilterForm = ({ query, onSearch, onClear }: Props) => {
  const user = useAppSelector((state) => state.user);
  const { id: privateMoneyId } = useAppSelector(selectUserPrivateMoney);

  const defaultValues = {
    shop_name: query.shop_name ?? '',
    shop_address: query.shop_address ?? '',
    shop_tel: query.shop_tel ?? '',
    shop_email: query.shop_email ?? '',
    is_include_members: query.is_include_members ?? false,
  };

  const { handleSubmit, control, reset, register } = useForm<FormFilterParams>({
    defaultValues,
  });

  const isIssuerMoney = (adminUser: AdminUser) => {
    return privateMoneyId ? adminUser.isIssuerOf(privateMoneyId) : false;
  };

  const onSubmit = (data: FormFilterParams) => {
    onSearch(removeEmptyKeysFromQuery({ ...data, page: 1 }));
  };

  useEffect(() => {
    reset({
      ...defaultValues,
    });
  }, [query]);

  return (
    <>
      <SelectedPrivateMoneyDisplay />
      <Wrapper>
        <FormItem>
          <LabeledTextBox labelText={<Label>店舗名</Label>} {...register('shop_name')} />
        </FormItem>
        <FormItem>
          <LabeledTextBox labelText={<Label>住所</Label>} {...register('shop_address')} />
        </FormItem>
        <FormItem>
          <LabeledTextBox labelText={<Label>電話番号</Label>} {...register('shop_tel')} />
        </FormItem>
        {(isIssuerMoney(user) || user.isServiceUser()) && (
          <FormItem>
            <Label>加盟店を含む</Label>
            <Controller
              control={control}
              name="is_include_members"
              render={({ field: { value, onChange } }) => (
                <CheckBox
                  id="is_include_members"
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              )}
            />
          </FormItem>
        )}
      </Wrapper>
      <FilterActions>
        <Button
          variant="neutral"
          onClick={() => {
            onClear();
            reset({ ...defaultValues });
          }}
        >
          クリア
        </Button>
        <Button onClick={handleSubmit(onSubmit)}>検索</Button>
      </FilterActions>
    </>
  );
};

export default FilterForm;
